/**
 * SAMWISE — Companion Orchestrator
 *
 * Manages Sam's presence in the app: when he greets, what he says,
 * how he responds to events (level selection, activity start, room presence).
 *
 * "Sam does not rush. Sam does not judge. Sam stays with the Pupil."
 */

import { SamwiseVoice } from './samwise-voice';
import {
  GREETINGS_BY_LEVEL,
  ACTIVITY_ENCOURAGEMENTS,
  FAREWELLS_BY_LEVEL,
  SEEDLING_CONVERSATIONS,
  KINDLING_CONVERSATIONS,
  MENTOR_DETECTED_LINES,
  MENTOR_LEFT_LINES,
  getTimeOfDayPrefix,
  pickRandom,
  type ConversationPrompt,
  type SamwiseGreeting,
} from './samwise-persona';
import type { RoomPresence } from '@aegis/feature-stream';

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export interface SamwiseCallbacks {
  /** Called when Sam starts or stops speaking */
  onSpeakingChange: (speaking: boolean) => void;
  /** Called when Sam has a line to display (text bubble) */
  onDialogue: (text: string) => void;
  /** Called when Sam offers conversation choices */
  onChoices: (prompt: ConversationPrompt) => void;
  /** Called when Sam's dialogue should be cleared */
  onDialogueClear: () => void;
}

// ---------------------------------------------------------------------------
// Companion
// ---------------------------------------------------------------------------

export class SamwiseCompanion {
  private voice: SamwiseVoice;
  private callbacks: SamwiseCallbacks;
  private currentLevel: string | null = null;
  private hasGreeted = false;
  private lastMentorState = false;
  private muted = false;
  private dialogueTimeout: number | null = null;

  constructor(callbacks: SamwiseCallbacks) {
    this.callbacks = callbacks;
    this.voice = new SamwiseVoice({
      onSpeakingChange: (speaking) => {
        this.callbacks.onSpeakingChange(speaking);
      },
    });
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Greet the student when they select a developmental level.
   */
  async greetForLevel(levelId: string): Promise<void> {
    this.currentLevel = levelId;
    this.hasGreeted = true;

    const greetings = GREETINGS_BY_LEVEL[levelId] ?? GREETINGS_BY_LEVEL['seedling'];
    const greeting = pickRandom(greetings);
    const timePrefix = getTimeOfDayPrefix();

    // Combine time-of-day with greeting
    const fullGreeting = `${timePrefix} ${greeting.text}`;

    await this.say(fullGreeting);

    // After greeting, offer a conversation starter
    await this.delay(800);
    this.offerConversation(levelId);
  }

  /**
   * Encourage during an activity.
   */
  async encourageActivity(activityId: string): Promise<void> {
    const lines = ACTIVITY_ENCOURAGEMENTS[activityId];
    if (!lines) return;

    const line = pickRandom(lines);
    await this.say(line);
  }

  /**
   * Say farewell when leaving a level/session.
   */
  async farewell(): Promise<void> {
    const level = this.currentLevel ?? 'seedling';
    const lines = FAREWELLS_BY_LEVEL[level] ?? FAREWELLS_BY_LEVEL['seedling'];
    const line = pickRandom(lines);
    await this.say(line);
    this.hasGreeted = false;
  }

  /**
   * Handle a conversation choice from the pupil.
   */
  async handleChoice(promptId: string, choiceIndex: number): Promise<void> {
    const conversations = this.getConversationsForLevel(this.currentLevel ?? 'seedling');
    const prompt = conversations.find(c => c.id === promptId);
    if (!prompt || !prompt.responses[choiceIndex]) return;

    const response = prompt.responses[choiceIndex];
    await this.say(response.samReply);
  }

  /**
   * React to room presence changes.
   */
  async onRoomPresenceChange(presence: RoomPresence): Promise<void> {
    const mentorNow = presence.mentor_detected;

    if (mentorNow && !this.lastMentorState) {
      // Mentor just arrived
      const line = pickRandom(MENTOR_DETECTED_LINES);
      await this.say(line);
    } else if (!mentorNow && this.lastMentorState) {
      // Mentor just left
      const line = pickRandom(MENTOR_LEFT_LINES);
      await this.say(line);
    }

    this.lastMentorState = mentorNow;
  }

  /**
   * Mute/unmute Sam's voice (text still shows).
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.voice.stop();
    }
  }

  /**
   * Stop all speech.
   */
  stop(): void {
    this.voice.stop();
    this.clearDialogue();
  }

  // -------------------------------------------------------------------------
  // Private helpers
  // -------------------------------------------------------------------------

  /**
   * Display text and speak it.
   */
  private async say(text: string): Promise<void> {
    // Show text bubble
    this.callbacks.onDialogue(text);

    // Speak (unless muted)
    if (!this.muted) {
      await this.voice.speak(text);
    } else {
      // Even if muted, hold the text for reading time
      await this.delay(Math.max(2000, text.length * 60));
    }

    // Keep text visible briefly after speech ends
    this.scheduleDialogueClear(3000);
  }

  /**
   * Offer conversation choices for the current level.
   */
  private offerConversation(levelId: string): void {
    const conversations = this.getConversationsForLevel(levelId);
    if (conversations.length === 0) return;

    const prompt = pickRandom(conversations);
    this.callbacks.onDialogue(prompt.samSays);
    this.callbacks.onChoices(prompt);

    if (!this.muted) {
      this.voice.speak(prompt.samSays);
    }
  }

  private getConversationsForLevel(levelId: string): ConversationPrompt[] {
    switch (levelId) {
      case 'seedling':
      case 'blossom':
        return SEEDLING_CONVERSATIONS;
      case 'kindling':
        return KINDLING_CONVERSATIONS;
      default:
        return SEEDLING_CONVERSATIONS;
    }
  }

  private scheduleDialogueClear(ms: number): void {
    if (this.dialogueTimeout) {
      clearTimeout(this.dialogueTimeout);
    }
    this.dialogueTimeout = window.setTimeout(() => {
      this.callbacks.onDialogueClear();
      this.dialogueTimeout = null;
    }, ms);
  }

  private clearDialogue(): void {
    if (this.dialogueTimeout) {
      clearTimeout(this.dialogueTimeout);
      this.dialogueTimeout = null;
    }
    this.callbacks.onDialogueClear();
  }

  private delay(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}
